/**
 * SheetPreview — l'aperçu de la fiche en feuilles A4 successives.
 *
 * Le contenu est rendu dans les conditions EXACTES de WeasyPrint (A4 =
 * 794 × 1123 px CSS, marges et corps du PDF « classic »), puis mis à
 * l'échelle pour l'écran : ce que l'utilisateur voit correspond au PDF.
 *
 * Une différence assumée demeure : à l'impression, un exercice trop long est
 * coupé entre deux pages ; ici il reste entier (plus lisible à l'écran). Le
 * nombre de feuilles peut donc être légèrement supérieur au PDF — c'est dit
 * sous l'aperçu, plutôt qu'annoncé comme définitif.
 */
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import MathHtmlRenderer from "../../lib/MathHtmlRenderer";
import "../../styles/sheet.css";
import "../../styles/exercice.css";

const ExerciseBlock = ({ ex, index, total, vue, tools, onMove, onRemove, rerollMenu,
                         signalerMenu }) => (
  <div className="lmm-ex">
    <div className="lmm-ex-header">
      <span className="lmm-ex-num">{index + 1}</span>
      <span className="lmm-ex-title">Exercice {index + 1}</span>
      {tools && (
        <span className="lmm-ex-tools">
          <button type="button" aria-label={`Monter l'exercice ${index + 1}`} disabled={index === 0}
            onClick={() => onMove(index, index - 1)}>↑</button>
          <button type="button" aria-label={`Descendre l'exercice ${index + 1}`} disabled={index === total - 1}
            onClick={() => onMove(index, index + 1)}>↓</button>
          {rerollMenu ? rerollMenu(ex, index) : null}
          {/* Signaler se fait ICI, devant l'exercice fautif. C'est le seul
              endroit où l'on voit le problème ; l'obliger à retrouver
              l'exercice ailleurs, c'est s'assurer que personne ne signale. */}
          {signalerMenu ? signalerMenu(ex, index) : null}
          <button type="button" aria-label={`Retirer l'exercice ${index + 1} de la fiche`}
            onClick={() => onRemove(ex.uniqueId)}>✕</button>
        </span>
      )}
    </div>
    <div className="lmm-ex-body">
      {/* `lmm-a4` dit à la CSS mobile de ne pas empiler les cellules ici :
          cette page fait 794 px, pas la largeur de l'écran. */}
      <MathHtmlRenderer html={ex.enonce_html} className="lmm-a4" />
      {(ex.figure_svg_enonce || ex.figure_svg) && (
        <div className="lmm-ex-figure" dangerouslySetInnerHTML={{ __html: ex.figure_svg_enonce || ex.figure_svg }} />
      )}
      {vue === "corrige" && (
        <div className="lmm-sol">
          <MathHtmlRenderer html={ex.solution_html} className="lmm-a4" />
          {ex.figure_svg_solution && (
            <div className="lmm-ex-figure" dangerouslySetInnerHTML={{ __html: ex.figure_svg_solution }} />
          )}
        </div>
      )}
    </div>
  </div>
);

const SheetHeader = ({ title, vue }) => (
  <div className="lmm-sheet-header">
    <div className="lmm-title">{title || "Fiche d'exercices"}{vue === "corrige" ? " — Corrigé" : ""}</div>
    <div className="lmm-meta">
      {vue === "corrige"
        ? [<span key="a">Réservé à l'enseignant</span>, <span key="b">Réponses et étapes détaillées</span>]
        : [<span key="a">Nom : ........................</span>, <span key="b">Classe : ..........</span>, <span key="c">Date : ......../......../........</span>]}
    </div>
  </div>
);

export default function SheetPreview({
  title, exercises, vue, onMove, onRemove, rerollMenu, signalerMenu, editable = true,
  // Une feuille A4 vide sous un exercice court porte du sens dans l'atelier —
  // « voici la page que vous fabriquez ». Dans un aperçu d'UN exercice, elle
  // n'en porte aucun : elle ajoute 700 px de blanc à traverser pour atteindre
  // le corrigé. On laisse alors la feuille suivre son contenu.
  ajusterAuContenu = false,
}) {
  const measurerRef = useRef(null);
  const containerRef = useRef(null);
  const [pages, setPages] = useState(null); // null => pas encore mesuré : une seule page
  const [scale, setScale] = useState(1);    // largeur disponible / 794px (A4 en px CSS)

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const apply = () => setScale(Math.min(1, (el.clientWidth || 794) / 794));
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Signature de contenu : re-mesurer quand la fiche change réellement
  // Ajustement final : la hauteur du cadre suit le contenu RÉELLEMENT rendu
  // (une estimation laissait quelques dizaines de pixels tronqués).
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    root.querySelectorAll("[data-sheet-frame]").forEach((frame) => {
      const page = frame.querySelector(".lmm-page");
      if (!page) return;
      const h = Math.max(PAGE_H, page.scrollHeight) * scale;
      frame.style.height = `${Math.ceil(h)}px`;
    });
  });

  const contentKey = useMemo(
    () => vue + "|" + (title || "") + "|" + exercises.map((e) => e.uniqueId).join(","),
    [exercises, vue, title]
  );

  useLayoutEffect(() => {
    const measurer = measurerRef.current;
    if (!measurer) return undefined;

    const compute = () => {
      const inner = measurer.querySelector(".lmm-inner");
      if (!inner) return;
      const usable = inner.clientHeight;                     // hauteur utile d'une page (px CSS, échelle PDF)
      const headerH = inner.querySelector(".lmm-sheet-header")?.offsetHeight || 0;
      const blocks = Array.from(inner.querySelectorAll(":scope > .lmm-ex"));
      const result = [];
      let current = [];
      let used = headerH;
      let left = usable - headerH;
      blocks.forEach((el, i) => {
        const h = el.offsetHeight + Math.round(0.9 * 14);    // + marge basse approx.
        // À l'impression, un exercice trop long COMMENCE sur la page courante
        // et se poursuit sur la suivante. On reproduit ce remplissage : tant
        // qu'il reste une bonne moitié de feuille, on ne renvoie pas le bloc
        // à la feuille d'après (sinon l'aperçu montre des feuilles à moitié
        // vides que le PDF n'aura pas).
        const placeRestante = left > usable * 0.45;
        if (current.length > 0 && h > left && !placeRestante) {
          result.push({ idxs: current, contentH: used });
          current = [];
          used = 0;
          left = usable;                                     // pages suivantes : pas d'en-tête
        }
        current.push(i);
        used += h;
        left -= h;
      });
      if (current.length > 0 || result.length === 0) {
        result.push({ idxs: current, contentH: used });
      }
      setPages(result);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(measurer);
    return () => ro.disconnect();
  }, [contentKey]);

  const pageList = pages && pages.length > 0
    ? pages
    : [{ idxs: exercises.map((_, i) => i), contentH: 0 }];
  // Hauteur d'une feuille : l'A4, ou davantage si un exercice long dépasse —
  // à l'écran on ne tronque jamais le contenu (le PDF, lui, le coupera).
  const PAGE_H = ajusterAuContenu ? 0 : 1123;
  const empty = exercises.length === 0;

  return (
    // `overflow: hidden` n'est pas cosmétique : la page de MESURE ci-dessous
    // fait 794 px de large et reste dans le flux de défilement, même invisible.
    // `visibility: hidden` la cache, elle ne la retire ni de la mise en page ni
    // du `scrollWidth`. Sans ce clip, le volet « Ma fiche » du mobile mesurait
    // 806 px de contenu pour 390 px d'écran — 416 px de défilement horizontal
    // vers du vide. Mesuré en émulation iPhone sur la production.
    <div ref={containerRef} style={{ position: "relative", overflow: "hidden" }}>
      {/* page de MESURE : taille A4 réelle (794px), jamais mise à l'échelle */}
      <div className={"lmm-page lmm-measurer" + (ajusterAuContenu ? " lmm-page-libre" : "")}
        ref={measurerRef} aria-hidden="true">
        <div className="lmm-inner" style={{ height: "100%" }}>
          <SheetHeader title={title} vue={vue} />
          {exercises.map((ex, i) => (
            <ExerciseBlock key={"m-" + vue + ex.uniqueId} ex={ex} index={i} total={exercises.length}
              vue={vue} tools={false} />
          ))}
        </div>
      </div>

      {/* pages réelles */}
      {pageList.map(({ idxs, contentH }, p) => (
        <div className="lmm-sheet-frame" key={"p" + p} data-sheet-frame
          style={{ height: Math.max(PAGE_H, contentH + (ajusterAuContenu ? 90 : 140)) * scale, aspectRatio: "auto" }}>
        <div className={"lmm-page" + (ajusterAuContenu ? " lmm-page-libre" : "")}
          style={{ "--lmm-scale": scale }}>
          {p === 0 && <SheetHeader title={title} vue={vue} />}
          {idxs.map((i) => exercises[i] && (
            <ExerciseBlock key={vue + exercises[i].uniqueId} ex={exercises[i]} index={i}
              total={exercises.length} vue={vue}
              tools={editable && vue === "sujet"}
              onMove={onMove} onRemove={onRemove} rerollMenu={rerollMenu}
              signalerMenu={signalerMenu} />
          ))}
          {/* ÉTAT VIDE INTENTIONNEL — écran seulement.
              Mesuré : la feuille vide laissait 490 px de blanc d'un seul tenant,
              un tiers de la page, et le premier écran de l'atelier était à
              moitié vide. On ne raccourcit pas la feuille — ses proportions A4
              portent le modèle mental « voici la page que vous fabriquez » —
              on l'HABITE : l'invitation, puis trois emplacements en attente.
              Une page qui attend, au lieu d'une page cassée.

              Ce bloc ne peut pas contaminer la pagination : la page de MESURE
              plus haut ne rend que l'en-tête et les exercices, jamais cet état.
              Et le PDF est composé par le serveur, qui ignore tout de ce fichier. */}
          {p === 0 && empty && (
            <div className="lmm-vide">
              <p className="lmm-vide-appel">
                <strong>Votre fiche vous attend.</strong>
                Choisissez un chapitre, générez des exercices, puis gardez ceux
                qui conviennent : ils s'installeront ici, tels qu'ils seront imprimés.
              </p>
              {[1, 2, 3].map((n) => (
                <div className="lmm-fantome" key={n} aria-hidden="true">
                  <span className="lmm-fantome-num">{n}</span>
                  <span className="lmm-fantome-titre">Exercice {n}</span>
                </div>
              ))}
            </div>
          )}
          {pageList.length > 1 && (
            <div className="lmm-page-num">Feuille {p + 1} / {pageList.length}</div>
          )}
        </div>
        </div>
      ))}

      {/* Honnêteté d'aperçu : le contenu est à l'échelle exacte du PDF, mais
          l'impression peut couper un long exercice entre deux pages, ce que
          l'aperçu ne fait pas (il le garde entier, plus lisible à l'écran).
          On le dit une fois, discrètement, plutôt que d'afficher un compte de
          pages présenté comme définitif. */}
      {pageList.length > 1 && (
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Aperçu à l'échelle du PDF — à l'impression, un exercice long peut se
          poursuivre sur la page suivante.
        </p>
      )}
    </div>
  );
}
